// =============================================================================
// ui/tabs/CoachFixIt.tsx — step 2, and the form that freezes a baseline.
// -----------------------------------------------------------------------------
// ── 🚨 A RULE IS TRADER-WIDE. THE ACCOUNT PICKER DOES NOT REACH IT ──
//
// Spot it narrows to whichever account is selected, because looking at one
// account is analysis. A RULE is not analysis — it is a habit, and the old
// client says so outright: the loop is "TRADER-WIDE (a leak is a behaviour that
// follows you across accounts)".
//
// It also has to be, mechanically. A baseline stores a number, a sample size and
// two dates — NOT which accounts it was measured over. So a baseline frozen with
// one account selected and re-proved across the book (or the reverse) would
// compare two different populations and report the difference as an improvement
// the trader never made. That is the comparator-is-a-population trap, and the
// only closure that does not need a new stored field is to make both ends
// trader-wide.
//
// ⚠ THE FORM SAYS SO, in the sentence above the box. A trader who narrowed to
// one account and then pressed Fix it would otherwise reasonably assume the
// rule was about that account.
//
// ── 🚨 THE BASELINE WINDOW COMES FROM THE TRADES, NOT THE PICKER ──
//
// `leakWindowOf` takes the first and last date present. The old client does the
// same, and the reason is the same one the tab owns its own date scope for: a
// baseline frozen against a window that moves is not a baseline.
// =============================================================================

import { useCallback, useMemo } from 'react';
import { EduMark } from '../components/EduMark';
import { useTabMemoryDraft } from '../state/tabMemory';
import { money, num } from '../format';
import {
  baselineUsable, canFixLeak, captureLeakBaseline, leakById, leakWindowOf,
  type LeakBaseline, type LeakContext, type LeakRule, type LeakTrade,
} from '../../selectors/leaks';

/** A leak's number in its own unit — dollars, a percentage, or minutes. */
export function leakValue(
  v: number | null, unit: 'usd' | 'pct' | 'min', decimals?: number,
): string {
  if (v === null) return '—';
  if (unit === 'usd') return money(v, decimals ?? 2);
  // ⚠ SIGNED, and only here. A hold-time gap is "losers held N minutes LONGER
  // than winners", so a negative one is the trader doing the right thing and the
  // sign carries the whole meaning.
  if (unit === 'min') return `${v >= 0 ? '+' : '−'}${num(Math.abs(v), decimals ?? 0)}m`;
  return `${num(v, decimals ?? 1)}%`;
}

/**
 * Two readings of one measure, printed so they cannot look identical.
 *
 * ── 🚨 THE SCREEN WAS CONTRADICTING ITSELF ──
 *
 * On Josh's own book the disposition rule showed **Baseline −2m · Now −2m ·
 * Change −24%**. Three figures, two of them identical, and a third insisting they
 * differ. The underlying values were about −2.4 and −1.8; minutes round to whole
 * numbers, so the change was real and invisible.
 *
 * ⚠ A DELIBERATE DIVERGENCE — the old client rounds minutes the same way and has
 * the same collision. It is not copied for the same reason its enabled-looking
 * "Bank it" was not: a reader who sees two equal numbers and a non-zero change
 * concludes the app is broken, and stops believing the next figure too.
 *
 * Precision is added only as far as it takes to separate them, so every reading
 * that never collides stays as short as it was.
 */
export function leakPair(
  baseline: number | null, current: number | null, unit: 'usd' | 'pct' | 'min',
): [string, string] {
  const at = (d?: number): [string, string] =>
    [leakValue(baseline, unit, d), leakValue(current, unit, d)];
  // Equal values SHOULD print the same — that is not a collision, it is the fact.
  if (baseline === null || current === null || baseline === current) return at();
  for (const d of [undefined, 1, 2]) {
    const pair = at(d);
    if (pair[0] !== pair[1]) return pair;
  }
  return at(2);
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export function FixIt(
  { rules, nextLeakId, onFix }: {
    rules: readonly LeakRule[];
    /** The biggest leak without a rule yet, or null when every one is covered. */
    nextLeakId: string | null;
    onFix: (leakId: string, prefill: string) => void;
  },
) {
  const next = nextLeakId ? leakById(nextLeakId) : null;

  return (
    <section className="card" data-testid="fix-it">
      <div className="coach-step-head">
        <span className="coach-step-n" aria-hidden="true">2</span>
        <div>
          <div className="coach-step-t">Fix it<EduMark eduKey="coach.fix" /></div>
          <p className="muted">
            {rules.length ? 'Your active rules' : 'Turn a leak into a rule'}
          </p>
        </div>
      </div>

      {rules.length ? (
        <div className="coach-rules">
          {rules.map((r) => {
            const leak = leakById(r.leakId);
            return (
              <div className="coach-rule" key={r.id} data-rule={r.id}>
                <div className="muted coach-rule-k">
                  Fixing · {leak ? leak.label : r.leakId}
                </div>
                <p className="coach-rule-text">{r.ruleText}</p>
                {/* ⚠ NO BASELINE VALUE HERE, and that is deliberate — it was
                    added, and Josh's side-by-side caught it. The figure lives in
                    the BASELINE tile in Prove it, directly below. Printing it
                    twice is D102: one screen states one fact once, and two
                    copies of a number invite the reader to check whether they
                    agree. The old client's line is exactly this. */}
                <div className="muted coach-rule-meta">
                  Baseline frozen · active since {r.createdAt || 'an earlier version'}.
                  {' '}Prove it (below) re-measures the same number.
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <p className="coach-fix-msg">
        {rules.length
          ? (next
            ? <>You’re running <b>{num(rules.length, 0)}</b> rule
              {rules.length === 1 ? '' : 's'}. Add one for another leak whenever
              you’re ready — each proves on its own baseline.</>
            : <>Every leak the report can measure already has a rule running.</>)
          : (next
            ? <>Pick a leak above and write the <b>rule</b> that closes it. Fix as
              many as you’ll actually work — each proves separately.</>
            : <>Nothing measurable to fix right now. A rule needs a leak with a
              number behind it, and this period has none.</>)}
      </p>

      {next ? (
        <button
          type="button"
          className={`btn${rules.length ? '' : ' primary'} coach-fix-cta`}
          onClick={() => onFix(next.id, '')}
        >Fix {next.label} →</button>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

export interface FixDialogProps {
  leakId: string;
  prefill: string;
  /** ⚠ TRADER-WIDE, already method-filtered. See the header. */
  trades: readonly LeakTrade[];
  ctx: LeakContext;
  busy: boolean;
  saveError: string | null;
  onCancel: () => void;
  /**
   * ⚠ RESOLVES TO WHETHER THE WRITE SUCCEEDED, and it has to (TCR-86). The
   * dialog throws the draft away on a save and keeps it on a refusal, and those
   * are the same click — only the caller knows which happened.
   */
  onSave: (baseline: LeakBaseline, text: string) => Promise<boolean>;
}

export function FixDialog(
  { leakId, prefill, trades, ctx, busy, saveError, onCancel, onSave }: FixDialogProps,
) {
  /**
   * ── 🔴 THE RULE OUTLIVES THE TAB, AND CANCEL STILL ASKS (TCR-86) ──
   *
   * This box holds the ONE output of Fix-it — the rule the trader writes for
   * themselves. It used to be `useState(prefill)`, and `AppShell` destroys the
   * whole tab subtree on every switch (D94), so walking over to the Trades tab
   * to check something threw the rule away in silence.
   *
   * ⚠ AUTOSAVE IS DELIBERATELY NOT THE FIX, and TCR-80 measured the line rather
   * than guessing it: every surface in this client that commits on blur is an
   * in-page card (six for six); the four modals hold a draft instead, because a
   * modal with a Cancel that discards cannot also be writing as you type.
   * Committing on blur here would additionally create a Fix-it record out of a
   * half-typed rule nobody pressed the button for.
   *
   * ⚠ KEYED BY `leakId`, so two leaks cannot inherit each other's rule. ⚠ A
   * page reload still loses it — `tabMemory` is session-scoped by design.
   */
  const [text, setText, discardDraft] = useTabMemoryDraft<string>(
    `coachfix:draft:${leakId}`, prefill,
  );
  const leak = leakById(leakId);

  /**
   * 🔴 "HAS THE TRADER TYPED ANYTHING" IS NOT "IS THE BOX NON-EMPTY".
   *
   * The box is SEEDED with suggested text the caller hands in, so a dirty check
   * against the empty string would prompt over a rule nobody touched — and a
   * guard that asks unconditionally is indistinguishable from no guard at all.
   * ⚠ Trimmed on both sides: a trailing newline is not an edit.
   */
  const dirty = text.trim() !== prefill.trim();

  /**
   * Cancel and ×. ⚠ The confirm comes FIRST and the discard only after it is
   * answered — an unconditional `discardDraft()` would throw the rule away and
   * then ask whether to.
   */
  const closeWithGuard = useCallback(() => {
    if (dirty && !window.confirm('You have an unsaved rule here. Leave it?')) return;
    discardDraft();
    onCancel();
  }, [dirty, discardDraft, onCancel]);

  /**
   * ⚠ DISCARDS ON SUCCESS ONLY. A refused write keeps the dialog open with the
   * typing in it — the behaviour this file already had and must not lose — and
   * the draft has to survive that refusal too, or walking away after a failed
   * save loses the rule a second time.
   *
   * ⚠ `discardDraft` after the caller has closed the dialog is safe on purpose:
   * the hook's discard is a bare store delete with no state update, which its
   * header says is exactly so it can run while the component is going away.
   */
  const save = useCallback(
    async (b: LeakBaseline) => { if (await onSave(b, text.trim())) discardDraft(); },
    [onSave, text, discardDraft],
  );

  const win = useMemo(() => leakWindowOf(trades as readonly { date: string }[]), [trades]);
  const baseline = useMemo(
    () => captureLeakBaseline(leakId, trades, ctx, win.from, win.to),
    [leakId, trades, ctx, win],
  );

  // ⚠ BOTH GATES, and they say different things. `canFixLeak` refuses a leak this
  // client cannot measure at all; `baselineUsable` refuses a number that cannot
  // be improved on. A trader hitting the second one should change the period, not
  // give up on the leak.
  const fixable = canFixLeak(leakId);
  const usable = useMemo(
    () => baselineUsable(leak, baseline), [leak, baseline],
  );
  const blocked = !fixable.ok || !usable.ok;

  // ⚠ AFTER the hooks, never before — an early return above them changes the
  // hook count between renders and React throws.
  if (!leak) return null;

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true"
      aria-label={`Fix it — ${leak.label}`}>
      <div className="modal">
        <header className="modal-head">
          <b>Fix it — {leak.label}</b>
          <button type="button" className="btn" onClick={closeWithGuard} disabled={busy}>×</button>
        </header>

        <div className="modal-body">
          <div className="muted coach-k">Baseline window</div>
          <div className="mono coach-fix-win">
            {win.from ?? '—'} → {win.to ?? '—'}
          </div>

          <p className="coach-fix-base">
            <b>{leak.label}</b> —{' '}
            <b className="mono">{leakValue(baseline?.value ?? null, leak.unit)}</b>
            {' '}over <b>{num(baseline?.n ?? 0, 0)}</b> qualifying trade
            {baseline?.n === 1 ? '' : 's'}.
          </p>

          {/* ⚠ SAID BEFORE THE BOX, not after. A trader who narrowed to one
              account upstairs would otherwise assume this rule is about it. */}
          <p className="muted coach-fix-scope">
            Measured across <b>every account</b> that counts, not just the one
            selected above — a habit follows you between accounts, so the rule
            has to as well.
          </p>

          {!fixable.ok ? (
            <div className="coach-warn bad" role="alert">
              <b>This leak can’t take a rule.</b> {fixable.reason}
            </div>
          ) : !usable.ok ? (
            <div className="coach-warn bad" role="alert">
              <b>This baseline can’t be used.</b> {usable.reason} Change the
              period above, or pick a different leak.
            </div>
          ) : null}

          {leak.kind === 'opportunity' && fixable.ok ? (
            // ⚠ NEVER SEPARATED FROM THE NUMBER. An opportunity figure is an
            // upper bound, and a blanket "hold longer" rule pays for the runners
            // with the stop-outs.
            <div className="coach-warn warn">
              <b>This is a counterfactual, not booked money.</b> Write a{' '}
              <b>conditional</b> rule — trail the stop, or hold only under a
              specific setup. A blanket “hold longer” pays for the runners with
              the stop-outs.
            </div>
          ) : null}

          <label className="field full coach-fix-field">
            <span className="muted coach-k">Your one rule</span>
            <textarea
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Trail the stop to break-even once price runs 1R"
            />
          </label>

          <p className="muted coach-fix-note">
            Saving freezes this baseline <i>and</i> your expectancy today. Prove
            it re-measures the same number, then judges separately whether you
            followed the rule and whether your edge held while you did.
          </p>

          {saveError ? (
            <div className="coach-warn bad" role="alert">{saveError}</div>
          ) : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn" onClick={closeWithGuard} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={blocked || busy || !text.trim() || !baseline}
            onClick={() => { if (baseline) void save(baseline); }}
          >{busy ? 'Saving…' : 'Freeze baseline & start'}</button>
        </footer>
      </div>
    </div>
  );
}
